import {catchError, map, Observable, of, ReplaySubject, tap} from "rxjs";
import {environment} from "@env/environment";
import {HttpClient, HttpParams} from "@angular/common/http";
import {Injectable} from "@angular/core";
import {AttestationBatch} from "@core/services/attestations/attestation-batch/attestation-batch.model";
import {
  AttestationSimpleRetraitTransaction,
  AttestationTransaction
} from "@core/services/attestations/attestation-batch/attestation-transaction.model";
import {SpringPage} from "@core/services/pricing/shared/spring-page.model";

@Injectable({
  providedIn: 'root'
})
export class AttestationBatchService {

  baseUrl = environment.numeric_attestations_url + '/batches';
  private _attestationBatch: ReplaySubject<AttestationBatch> = new ReplaySubject<AttestationBatch>(1);
  private _attestationBatches: ReplaySubject<AttestationBatch[]> = new ReplaySubject<AttestationBatch[]>(1);

  set attestationBatch(value: AttestationBatch) {
    this._attestationBatch.next(value);
  }

  get attestationBatch$() {
    return this._attestationBatch.asObservable();
  }

  set attestationBatches(value: AttestationBatch[]) {
    this._attestationBatches.next(value);
  }

  get attestationBatches$() {
    return this._attestationBatches.asObservable();
  }

  constructor(
    private _httpClient: HttpClient
  ) {
  }

  create(attestationBatch: AttestationBatch): Observable<AttestationBatch> {
    return this._httpClient.post<AttestationBatch>(`${this.baseUrl}`, attestationBatch)
      .pipe(
        tap((response: AttestationBatch) => {
          this.attestationBatch = new AttestationBatch(response);
          return this.attestationBatch;
        }),
        catchError(() => of({} as AttestationBatch))
      );
  }

  getAll(): Observable<AttestationBatch[]> {
    return this.getPage(0, 1000).pipe(
      map(page => page.content),
      catchError(() => of([]))
    );
  }

  getPage(page = 0, size = 10, sort?: string, referenceUuid?: string, exerciseYear?: number): Observable<SpringPage<AttestationBatch>> {
    let params = new HttpParams().set('page', page).set('size', size);
    if (sort) params = params.set('sort', sort);
    if (referenceUuid) params = params.set('referenceUuid', referenceUuid);
    if (exerciseYear != null) params = params.set('exerciseYear', exerciseYear.toString());
    return this._httpClient.get<SpringPage<AttestationBatch>>(this.baseUrl, {params})
      .pipe(
        map((response: SpringPage<AttestationBatch>) => ({
          ...response,
          content: (response?.content || []).map(item => new AttestationBatch(item))
        })),
        tap(p => this._attestationBatches.next(p.content)),
        catchError(() => of({
          content: [], totalElements: 0, totalPages: 0, size,
          number: page, first: true, last: true, numberOfElements: 0, empty: true
        } as SpringPage<AttestationBatch>))
      );
  }

  update(attestationBatch: AttestationBatch): Observable<AttestationBatch> {
    return this._httpClient.put<AttestationBatch>(`${this.baseUrl}/${attestationBatch.uuid}`, attestationBatch)
      .pipe(
        tap((response: AttestationBatch) => {
          this.attestationBatch = response;
          return response;
        }),
        catchError(() => of({} as AttestationBatch))
      );
  }

  delete(id: string): Observable<void> {
    return this._httpClient.delete<void>(`${this.baseUrl}/${id}`)
      .pipe(
        tap(() => {
          this.attestationBatches$
            .pipe(
              map(attestationBatches => attestationBatches.filter(attestationBatch => attestationBatch.uuid !== id))
            ).subscribe(updatedAttestationBatchs => this.attestationBatches = updatedAttestationBatchs);
        }),
        catchError(() => of())
      );
  }

  cancel(uuid: string, reason: string): Observable<AttestationBatch> {
    return this._httpClient.post<AttestationBatch>(`${this.baseUrl}/${uuid}/cancel`, { reason })
      .pipe(
        tap((response: AttestationBatch) => {
          const updated = new AttestationBatch(response);
          this.attestationBatch = updated;
          this.attestationBatches$
            .pipe(
              map(batches => batches.map(b => b.uuid === updated.uuid ? updated : b))
            )
            .subscribe(list => this.attestationBatches = list);
        }),
        catchError(() => of({} as AttestationBatch))
      );
  }

  doTransaction(attestationTransaction: AttestationTransaction): Observable<void> {
    return this._httpClient.post<void>(`${this.baseUrl}/transactions`, attestationTransaction)
      .pipe(catchError(() => of()));
  }

  doSimpleRetraitTransaction(attestationTransaction: AttestationSimpleRetraitTransaction): Observable<void> {
    return this._httpClient.post<void>(`${this.baseUrl}/transactions/simple-retrait`, attestationTransaction)
      .pipe(catchError(() => of()));
  }

}
